// @/components/reusable/table.jsx
// Fresh Table component based on Catalyst UI Kit defaults
// Keep it simple - use Catalyst's styling, don't over-customize

import React, { createContext, useContext, useState } from "react";
import { Link } from "gatsby";
import clsx from "clsx";

// ============================================================================
// CONTEXT
// ============================================================================

const TableContext = createContext({
  bleed: false,
  dense: false,
  grid: false,
  striped: false,
});

const TableRowContext = createContext({
  href: undefined,
  target: undefined,
  title: undefined,
});

// ============================================================================
// TABLE COMPONENTS
// ============================================================================

/**
 * Table wrapper component
 * @param {boolean} bleed - Allow content to bleed to edges
 * @param {boolean} dense - Use compact row padding
 * @param {boolean} grid - Show vertical grid lines
 * @param {boolean} striped - Alternate row backgrounds
 */
export function Table({
  bleed = false,
  dense = false,
  grid = false,
  striped = false,
  fixed = false,
  className,
  children,
  ...props
}) {
  return (
    <TableContext.Provider value={{ bleed, dense, grid, striped }}>
      <div className="flow-root">
        <div
          {...props}
          className={clsx(className, "-mx-(--gutter) overflow-x-auto whitespace-nowrap")}
        >
          <div
            className={clsx(
              "inline-block min-w-full align-middle",
              !bleed && "sm:px-(--gutter)"
            )}
          >
            <table className={clsx(
              "min-w-full text-left font-fono tracking-tighter text-sm/5 text-black dark:text-white border-b border-zinc-200/50 dark:border-white/5",
              fixed && "table-fixed"
            )}>
              {children}
            </table>
          </div>
        </div>
      </div>
    </TableContext.Provider>
  );
}

/**
 * Table head wrapper
 */
export function TableHead({ className, ...props }) {
  return (
    <thead
      {...props}
      className={clsx(className, "text-zinc-400 dark:text-zinc-400 text-[11px]/[1.25] bg-zinc-50 dark:bg-zinc-900 [&_tr:hover]:bg-transparent!")}
    />
  );
}

/**
 * Table body wrapper
 */
export function TableBody(props) {
  return <tbody {...props} />;
}

/**
 * Table row component
 * @param {string} href - Optional link for clickable rows
 * @param {string} target - Link target
 * @param {string} title - Accessible title for link
 */
export function TableRow({ href, target, title, className, children, ...props }) {
  const { striped } = useContext(TableContext);

  return (
    <TableRowContext.Provider value={{ href, target, title }}>
      <tr
        {...props}
        className={clsx(
          className,
          href &&
          "has-[[data-row-link][data-focus]]:outline-2 has-[[data-row-link][data-focus]]:-outline-offset-2 has-[[data-row-link][data-focus]]:outline-blue-500 dark:focus-within:bg-white/2.5",
          striped && "even:bg-zinc-50 dark:even:bg-white/2.5",
          striped && "hover:bg-blue-50/25 dark:hover:bg-white/5",
          !striped && "hover:bg-blue-50/25 dark:hover:bg-white/2.5"
        )}
      >
        {children}
      </tr>
    </TableRowContext.Provider>
  );
}

/**
 * Table header cell (th)
 */
export function TableHeader({ className, ...props }) {
  const { bleed, grid } = useContext(TableContext);

  return (
    <th
      {...props}
      className={clsx(
        className,
        "border-t border-t-zinc-200/50 border-b border-b-zinc-200/50 px-4 py-2 font-medium",
        "first:pl-(--gutter,--spacing(2)) last:pr-(--gutter,--spacing(2))",
        "dark:border-t-white/5 dark:border-b-white/5",
        grid && "border-l border-l-zinc-200/50 first:border-l-0 dark:border-l-white/5",
        !bleed && "sm:last:pr-1"
      )}
    />
  );
}

/**
 * Table data cell (td)
 */
export function TableCell({ className, children, ...props }) {
  const { bleed, dense, grid, striped } = useContext(TableContext);
  const { href, target, title } = useContext(TableRowContext);
  const [cellRef, setCellRef] = useState(null);

  return (
    <td
      ref={href ? setCellRef : undefined}
      {...props}
      className={clsx(
        className,
        "relative px-4",
        "first:pl-(--gutter,--spacing(2)) last:pr-(--gutter,--spacing(2))",
        !striped && "border-b border-zinc-200/50 dark:border-white/5",
        grid && "border-l border-l-zinc-200/50 first:border-l-0 dark:border-l-white/5",
        dense ? "py-4" : "py-8",
        !bleed && "sm:last:pr-1"
      )}
    >
      {href && (
        <Link
          data-row-link
          to={href}
          target={target}
          aria-label={title}
          tabIndex={cellRef?.previousElementSibling === null ? 0 : -1}
          className="absolute inset-0 focus:outline-hidden"
        />
      )}
      {children}
    </td>
  );
}
